<template>
    <div class="upsell-settings-summary">
        <ChargebeeUpsellSettingsSummaryItemsV3 v-if="getIsPricingVersion3" />
        <template v-else>
            <!-- LICENSES -->
            <div class="upsell-settings-summary-licenses">
                <h2 class="upsell-settings-summary-heading typographies-paragraph-large-semibold">
                    {{ $l10n.__('Licences') }}
                </h2>
                <ChargebeeUpsellSummaryItems :data="subscriptionDataLicenses" />
            </div>

            <!-- OPTIONS -->
            <div class="upsell-settings-summary-options">
                <h2 class="upsell-settings-summary-heading typographies-paragraph-large-semibold">
                    {{ $l10n.__('Options') }}
                </h2>
                <ChargebeeUpsellSummaryItems :data="subscriptionDataOptions" />
            </div>
        </template>

        <!-- INCOMING SUBSCRIPTION-->
        <div class="upsell-settings-summary-incoming">
            <h2 class="upsell-settings-summary-heading typographies-paragraph-large-semibold">
                <span>{{ $l10n.__('Nouvel abonnement') }}</span>
                <div>
                    <span class="typographies-paragraph-large-bold">
                        {{
                            humanizeCentsPrice(getSubscriptionIncoming.heading.price)
                                | numberformatCurrency
                        }}
                    </span>
                    <span class="typographies-paragraph-medium-semibold">
                        /{{ recurrencyLabel(getRecurrence) }}
                    </span>
                </div>
            </h2>
            <ul class="upsell-settings-summary-listing">
                <li
                    v-for="(item, index) in getSubscriptionIncoming.items"
                    :key="index"
                    class="upsell-settings-summary-listing__item typographies-paragraph-medium-regular"
                >
                    <span class="upsell-settings-summary-listing__item-label">
                        {{ item.label }}
                    </span>
                    <div class="upsell-settings-summary-listing__item-price">
                        <span class="typographies-paragraph-medium-semibold">
                            {{ humanizeCentsPrice(item.price) | numberformatCurrency }}
                        </span>
                        <span class="typographies-paragraph-small-regular">
                            /{{ recurrencyLabel(item.recurrence) }}
                        </span>
                    </div>
                </li>
            </ul>
        </div>

        <!-- CURRENT SUBSCRIPTION-->
        <div class="upsell-settings-summary-current">
            <h2 class="upsell-settings-summary-heading typographies-paragraph-large-semibold">
                <span>{{ $l10n.__('Abonnement en cours') }}</span>
                <div>
                    <span class="typographies-paragraph-large-bold">
                        {{
                            humanizeCentsPrice(subscriptionCurrent.heading.price)
                                | numberformatCurrency
                        }}
                    </span>
                    <span
                        v-if="subscriptionCurrent.heading.recurrence"
                        class="typographies-paragraph-medium-semibold"
                    >
                        /{{ recurrencyLabel(subscriptionCurrent.heading.recurrence) }}
                    </span>
                </div>
            </h2>
            <ul class="upsell-settings-summary-listing">
                <li
                    v-for="(item, index) in subscriptionCurrent.items"
                    :key="index"
                    class="upsell-settings-summary-listing__item typographies-paragraph-medium-regular"
                >
                    <span class="upsell-settings-summary-listing__item-label">
                        {{ item.label }}
                    </span>
                    <div class="upsell-settings-summary-listing__item-price">
                        <span class="typographies-paragraph-medium-semibold">
                            {{ humanizeCentsPrice(item.price) | numberformatCurrency }}
                        </span>
                        <span class="typographies-paragraph-small-regular">
                            /{{ recurrencyLabel(item.recurrence) }}
                        </span>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>
<script>
import ChargebeeUpsellSummaryItems from './ChargebeeUpsellSummaryItems.vue';
import ChargebeeUpsellMixin from 'mixins/chargebee/ChargebeeUpsellMixins';
import ChargebeeUpsellSettingsSummaryItemsV3 from 'vcomponents/pages/account/upsell/pricingVersion3/ChargebeeUpsellSettingsSummaryItemsV3.vue';
import { createNamespacedHelpers } from 'vuex';
const chargebeeUpsellStore = createNamespacedHelpers('chargebeeUpsellStore');
import {
    calculateDiscount,
    isOption,
    isSubOption,
} from 'js/vue/helpers/chargebee/chargebeeUpsellHelpers.js';

export default {
    components: { ChargebeeUpsellSummaryItems, ChargebeeUpsellSettingsSummaryItemsV3 },
    mixins: [ChargebeeUpsellMixin],
    computed: {
        ...chargebeeUpsellStore.mapGetters([
            'getSubscriptionItems',
            'getPreviousSubscriptionItems',
            'getRecurrence',
            'getDiscounts',
            'getSubscriptionIncoming',
            'getSubscriptionInvoicing',
            'getIsPricingVersion3',
        ]),
        subscriptionDataLicenses() {
            return this.getSubscriptionItems.filter(
                (item) => !isOption(item) && !isSubOption(item),
            );
        },
        subscriptionDataOptions() {
            return this.getSubscriptionItems.filter((item) => isOption(item) || isSubOption(item));
        },
        subscriptionCurrent() {
            const totalAmount = this.getPreviousSubscriptionItems.reduce(
                (sum, record) => sum + record.amount,
                0,
            );
            let totalDiscount = 0;
            for (const subscriptionItem of this.getPreviousSubscriptionItems) {
                totalDiscount += calculateDiscount({
                    subscriptionItem,
                    discounts: this.getDiscounts,
                });
            }
            const subscriptionCurrent = {
                heading: {
                    price: totalAmount,
                    recurrence: this.getRecurrence,
                },
                items: [
                    {
                        label: this.$l10n.__('Réduction appliquée'),
                        recurrence: this.getRecurrence,
                        price: totalDiscount,
                    },
                ],
            };
            if (!subscriptionCurrent?.items[1] && this.getSubscriptionInvoicing?.item) {
                subscriptionCurrent.items[1] = {
                    label: this.$l10n.__(`TVA ${this.getSubscriptionInvoicing.items[1].rate}%`),
                    price:
                        (subscriptionCurrent.heading.price *
                            this.getSubscriptionInvoicing.items[1].rate) /
                        100,
                };
            }
            return subscriptionCurrent;
        },
    },
};
</script>

<style lang="scss" scoped>
.upsell-settings-summary {
    display: flex;
    flex-direction: column;

    &-heading {
        display: flex;
        justify-content: space-between;
        margin: 0 0 var(--spacing-1) 0;
    }

    &-licenses {
        margin-bottom: var(--spacing-3);
    }
    &-options {
        padding-bottom: var(--spacing-4);
        border-bottom: solid 1px var(--colors-common-border-default);
    }
    &-incoming {
        margin: var(--spacing-3) 0;
        border-bottom: solid 1px var(--colors-common-border-lighter);
        .upsell-settings-summary-listing__item-label {
            color: var(--colors-common-text-subtle-default);
        }
    }
    &-incoming,
    &-current {
        .upsell-settings-summary-heading {
            margin-bottom: 0;
        }
    }
    &-current {
        color: var(--colors-common-text-standard-disabled);
    }

    &-listing {
        margin: 0 0 var(--spacing-3) 0;

        &__item {
            display: flex;
            justify-content: space-between;

            &-label {
                display: flex;
                align-items: center;
            }
        }
    }
}
</style>
